import cn from 'classnames'
import {useEffect, useRef, useState} from 'react'

import {BTN_PRIMARY} from '../constants'
import {useQuestionTimer} from '../hooks'
import type {Question, QuestionAnswerTiming} from '../types'
import {ProgressBar} from './ProgressBar'
import {TimerBadge} from './TimerBadge'

export const PhishingEmail = ({
  question,
  questionNumber,
  totalQuestions,
  onAnswerLocked,
  onAnswer,
}: {
  question: Question
  questionNumber: number
  totalQuestions: number
  onAnswerLocked: (answer: string, score: number, correct: boolean, timing: QuestionAnswerTiming) => void
  onAnswer: (answer: string, score: number, correct: boolean, timing: QuestionAnswerTiming) => void
}) => {
  const email = question.phishingEmail!
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null)
  const [answered, setAnswered] = useState(false)
  const [earnedScore, setEarnedScore] = useState(0)
  const [isCorrect, setIsCorrect] = useState(false)
  const [answerTiming, setAnswerTiming] = useState<QuestionAnswerTiming | null>(null)
  const startedAtRef = useRef(Date.now())

  const getAnswerTiming = (timedOut: boolean, remainingSeconds: number): QuestionAnswerTiming => ({
    elapsedMs: Date.now() - startedAtRef.current,
    remainingSeconds,
    timedOut,
  })

  const handleFieldClick = (fieldId: string) => {
    if (answered) {
      return
    }

    const correct = fieldId === email.correctFieldId
    const score = correct ? question.points : 0
    const timing = getAnswerTiming(false, remaining)

    setSelectedFieldId(fieldId)
    setAnswered(true)
    setIsCorrect(correct)
    setEarnedScore(score)
    setAnswerTiming(timing)
    onAnswerLocked(fieldId, score, correct, timing)
  }

  const handleTimerExpire = () => {
    if (answered) {
      return
    }

    setAnswered(true)
    setEarnedScore(0)
    setIsCorrect(false)

    const timing = getAnswerTiming(true, 0)
    setAnswerTiming(timing)
    onAnswerLocked('', 0, false, timing)
  }

  const {remaining, total} = useQuestionTimer(question.timerSeconds, answered, handleTimerExpire)

  const [entered, setEntered] = useState(false)
  const [exiting, setExiting] = useState(false)

  // Trigger enter animation shortly after mount
  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 50)
    return () => clearTimeout(t)
  }, [])

  const handleNext = () => {
    const timing = answerTiming ?? getAnswerTiming(false, remaining)

    if (questionNumber >= totalQuestions) {
      setExiting(true)
      setTimeout(() => onAnswer(selectedFieldId ?? '', earnedScore, isCorrect, timing), 500)
    } else {
      onAnswer(selectedFieldId ?? '', earnedScore, isCorrect, timing)
    }
  }

  const buttonLabel = questionNumber < totalQuestions ? 'Next question' : 'See results'
  const isRevealed = answered
  const correctFieldId = email.correctFieldId

  const fieldClasses = (fieldId: string) => {
    const isCorrectField = isRevealed && fieldId === correctFieldId

    return cn('relative cursor-pointer px-4 py-3 transition-colors', {
      'hover:bg-moss-grey-500/10': !answered,
      'outline outline-1 outline-green-neon bg-green-neon/10': isCorrectField && isCorrect,
      'outline outline-1 outline-ui-red-450 bg-ui-red-450/10': isCorrectField && !isCorrect,
      'opacity-40': isRevealed && fieldId !== correctFieldId,
    })
  }

  return (
    <div className="flex min-h-screen flex-col px-6 py-8 md:mx-auto md:w-full md:max-w-3xl">
      <div className="mb-10">
        <ProgressBar current={questionNumber} total={totalQuestions} />
      </div>

      <div
        className="flex h-[520px] flex-col overflow-y-auto transition-opacity duration-500 ease-out lg:flex-row lg:gap-8"
        style={{opacity: !entered || exiting ? 0 : 1}}
      >
        <div className="lg:w-[60%]">
          <div className="overflow-hidden rounded-lg border border-grey-900 bg-[#171717]">
            <div className="flex items-center gap-2 border-b border-moss-grey-500/20 bg-moss-grey-500/20 px-4 py-2.5">
              <div className="flex gap-1.5">
                <div className="size-2.5 rounded-full bg-ui-red-450/60" />
                <div className="size-2.5 rounded-full bg-orange-300/60" />
                <div className="size-2.5 rounded-full bg-green-neon/40" />
              </div>
              <div className="ml-3 flex-1 rounded bg-moss-grey-500/30 px-3 py-1 text-12 text-moss-grey-400">
                mail.google.com
              </div>
            </div>

            <div className="divide-y divide-grey-700 py-2">
              <div onClick={() => handleFieldClick('sender-address')} className={fieldClasses('sender-address')}>
                <span className="text-13 text-moss-grey-400">From</span>
                <p className="text-13">
                  <span className="font-medium text-moss-white">{email.senderName}</span>{' '}
                  <span
                    className={cn('text-moss-grey-300', {
                      'text-green-neon font-semibold': isRevealed && correctFieldId === 'sender-address' && isCorrect,
                      'text-ui-red-450 font-semibold': isRevealed && correctFieldId === 'sender-address' && !isCorrect,
                    })}
                  >
                    &lt;{email.senderAddress}&gt;
                  </span>
                </p>
                {isRevealed && correctFieldId === 'sender-address' && isCorrect && (
                  <span className="mt-1 block text-right text-14 font-semibold text-green-neon">
                    +{earnedScore} PTS
                  </span>
                )}
              </div>

              <div onClick={() => handleFieldClick('reply-to')} className={fieldClasses('reply-to')}>
                <span className="text-13 text-moss-grey-400">Reply-To</span>
                <p
                  className={cn('text-13', {
                    'text-moss-grey-300': !(isRevealed && correctFieldId === 'reply-to'),
                    'text-green-neon font-semibold': isRevealed && correctFieldId === 'reply-to' && isCorrect,
                    'text-ui-red-450 font-semibold': isRevealed && correctFieldId === 'reply-to' && !isCorrect,
                  })}
                >
                  {email.replyTo}
                </p>
                {isRevealed && correctFieldId === 'reply-to' && isCorrect && (
                  <span className="mt-1 block text-right text-14 font-semibold text-green-neon">
                    +{earnedScore} PTS
                  </span>
                )}
              </div>

              <div onClick={() => handleFieldClick('subject')} className={fieldClasses('subject')}>
                <span className="text-13 text-moss-grey-400">Subject</span>
                <p
                  className={cn('text-13 font-medium', {
                    'text-moss-white': !(isRevealed && correctFieldId === 'subject'),
                    'text-green-neon': isRevealed && correctFieldId === 'subject' && isCorrect,
                    'text-ui-red-450': isRevealed && correctFieldId === 'subject' && !isCorrect,
                  })}
                >
                  {email.subject}
                </p>
                {isRevealed && correctFieldId === 'subject' && isCorrect && (
                  <span className="mt-1 block text-right text-14 font-semibold text-green-neon">
                    +{earnedScore} PTS
                  </span>
                )}
              </div>

              <div onClick={() => handleFieldClick('body')} className={fieldClasses('body')}>
                <p
                  className={cn('whitespace-pre-line text-13 leading-relaxed', {
                    'text-moss-grey-300': !(isRevealed && correctFieldId === 'body'),
                    'text-green-neon': isRevealed && correctFieldId === 'body' && isCorrect,
                    'text-ui-red-450': isRevealed && correctFieldId === 'body' && !isCorrect,
                  })}
                >
                  {email.body}
                </p>
                {isRevealed && correctFieldId === 'body' && isCorrect && (
                  <span className="mt-1 block text-right text-14 font-semibold text-green-neon">
                    +{earnedScore} PTS
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col lg:mt-0 lg:w-[40%] lg:shrink-0">
          <div className="mb-1 flex items-center gap-2.5">
            <span className="text-12 font-medium uppercase tracking-[.08em] text-moss-grey-400">Level 2</span>
            <span className="flex items-center gap-1.5 text-10 text-grey-300">
              <span className="size-1 shrink-0 rounded-full bg-grey-300" />
              {question.points} points for the right answer
            </span>
          </div>

          <h2 className="mb-3 text-18 font-medium leading-tight text-moss-white md:text-22">{question.question}</h2>
          <p className="mb-8 text-13 leading-relaxed text-moss-grey-300">
            Fraud emails often look legitimate, but it&apos;s the small details that make the difference.
          </p>

          {answered && (
            <>
              <div className="mb-8 rounded-lg border border-grey-900 p-5">
                <div className="mb-3 flex items-center gap-2">
                  <div className="size-1.5 rounded-full bg-green-neon" />
                  <span className="text-12 font-semibold uppercase tracking-[.1em] text-green-neon">
                    Finance Insight
                  </span>
                </div>
                <p className="text-[15px] leading-relaxed text-moss-grey-300">{question.mossInsight.body}</p>
              </div>
            </>
          )}
        </div>
      </div>

      <div
        className="flex items-center justify-between pt-4 transition-opacity duration-500 ease-out"
        style={{opacity: !entered || exiting ? 0 : 1}}
      >
        <TimerBadge remaining={remaining} total={total} />
        <button onClick={handleNext} disabled={!answered} className={BTN_PRIMARY}>
          {buttonLabel}
        </button>
      </div>
    </div>
  )
}
